import { getCometBrowserInfo, getCometSource, isCometMobileBrowser, isCometOffTheRecordProfile, } from '#pplx/frontend/comet/client/utils.js';
import { parseUrl } from '#pplx/frontend/common/files/urls.js';
import { getApiClient } from '#pplx/frontend/fetcher/utils.js';
import { getChosenLocale } from '#pplx/frontend/i18n/browser.js';
import { defaultTimezone } from '#pplx/frontend/i18n/core.js';
import { logger } from '#pplx/frontend/logger/browser.js';
const WEB_BROWSER_EXTENSION_QUERY_SOURCES = new Set([
    'computer',
    'computer-followup',
    'home',
    'followup',
]);
const isURL = (str) => {
    try {
        new URL(str);
        return true;
    }
    catch {
        return false;
    }
};
const getClientSearchResultsCacheKey = ({ isCometBrowser, frontendUUID, shouldUseNavV3CacheKey, }) => {
    if (isCometBrowser && frontendUUID) {
        return `nav-${frontendUUID}`;
    }
    if (shouldUseNavV3CacheKey && frontendUUID) {
        return frontendUUID;
    }
    return undefined;
};
export const resolveTargetThreadAccessLevel = (override, 
// Accept readonly because `SubmitQueryParams.collection` flows in wrapped
// by `ReadonlyDeep<...>` at the caller; without `Readonly` the readonly
// → mutable mismatch fires under `moduleResolution: bundler`.
collection) => override ?? collection?.access ?? undefined;
export const getSourceParams = ({ isCometBrowser, isMobile, isWindowsApp, isLinuxOS, isMacOS, clientPayloadUUID, }) => {
    return isCometBrowser
        ? { source: getCometSource(), client_payload_cache_key: clientPayloadUUID }
        : {
            source: getApiClient({
                isMobile,
                isWindowsApp,
                isLinuxOS,
                isMacOS,
            }),
        };
};
export const createSubmitParams = ({ inPageOverride, dslQuery, existingEntryUUID, existingUuid, redoSearch, collection, isRelatedQuery, isSponsored, relatedQueryUUID, inDomain, inPage, newFrontendContextUUID, promptSource, querySource, timeFromFirstType, utmSource, disableWidgets, isNavSuggestionsDisabled, entropyBrowser, clientPayloadUUID, isIncognito, modelPreferenceSubmit, isAskDeepResearch, modeConfig, modelOverride, compareModelPreferences, lastBackendUUID, branchMode, deviceLocation, newAttachments, threadMetadataRwToken, forceNew, finalSources, mode, frontendUUID, isWindowsApp, isLinuxOS, isMacOS, isMobile, mentions, queryDisplayMetadata, skipSearchEnabled, sidecarTabId, sidecarUrl, sidecarTitle, sidecarSecondaryTabId, sidecarSecondaryUrl, sidecarSecondaryTitle, isFirstEntry, alwaysSearchOverride, overrideNoSearch, mcpTools, refinementFiltersMetadata, browserAgentAllowOnceFromToggle, forceEnableBrowserAgent, forceDisableBrowserAgent, enableLearnAboutMe, localWorkspaceDirectories, localFileGrants, inlineMode, canonicalPageContext, shouldUseNavV3CacheKey, supportedBlockUseCases, isLocalBrowserAvailable, isWebBrowserExtensionAvailable, policyApprovalDecision, targetThreadAccessLevelOverride, prefills, workflowInstructions, workflowConfig, workflowKey, sourceMetadata, getExperimentOverrides, getExperimentAssignments, renderingPlace, }) => {
    const experimentOverrides = getExperimentOverrides();
    const experimentAssignments = getExperimentAssignments?.() ?? {};
    const clientCoordinates = deviceLocation
        ? {
            location_lat: deviceLocation.latitude,
            location_lng: deviceLocation.longitude,
        }
        : null;
    const isCometBrowser = !!getCometBrowserInfo(); // isMaybeIncognitoComet without the Ask dependency
    // Log backend flag overrides if present
    if (Object.keys(experimentOverrides).length > 0) {
        logger.info('Sending backend flag overrides to API', {
            overrideCount: Object.keys(experimentOverrides).length,
            flagKeys: Object.keys(experimentOverrides),
            flagValues: experimentOverrides,
        });
    }
    const appClientContext = getSourceParams({
        isCometBrowser,
        isMobile,
        isWindowsApp,
        isLinuxOS,
        isMacOS,
        clientPayloadUUID,
    });
    let localSearchEnabled = false;
    let lastOmniboxInput = '';
    let riskyQuerySource = undefined;
    let isNavSuggestionsDisabledOverride = false;
    let sidecarQuerySourceOverride = undefined;
    if (import.meta.env.VITE_IS_SIDECAR_BUILD || import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD) {
        isNavSuggestionsDisabledOverride = true;
        sidecarQuerySourceOverride = querySource === 'followup' ? 'followup-sidecar' : 'sidecar';
    }
    if (import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD) {
        sidecarQuerySourceOverride = 'mobile-sidecar';
        isNavSuggestionsDisabledOverride = true;
        sidecarQuerySourceOverride =
            querySource === 'followup' ? 'followup-mobile-sidecar' : 'mobile-sidecar';
    }
    if (import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
        isNavSuggestionsDisabledOverride = true;
        sidecarQuerySourceOverride = 'inline-assistant';
    }
    let comet_info;
    if (isCometBrowser) {
        /**
         * Outside of sidecar we dont need url/tabid of active user tab
         */
        if (import.meta.env.VITE_IS_SIDECAR_BUILD ||
            import.meta.env.VITE_IS_EMBEDDED_SIDECAR_BUILD ||
            import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD ||
            import.meta.env.VITE_IS_INLINE_ASSISTANT_BUILD) {
            comet_info = {
                rendering_place: renderingPlace,
                sidecar_tab_id: sidecarTabId,
                sidecar_url: sidecarUrl,
                sidecar_title: sidecarTitle,
                sidecar_secondary_tab_id: sidecarSecondaryTabId,
                sidecar_secondary_url: sidecarSecondaryUrl,
                sidecar_secondary_title: sidecarSecondaryTitle,
            };
        }
        else {
            const isCometMobile = isCometMobileBrowser();
            comet_info = {
                rendering_place: isCometMobile ? `mobile-${renderingPlace}` : renderingPlace,
            };
        }
        const isIncognito = isCometOffTheRecordProfile();
        // Check both agent availability and incognito status
        const isAgentAvailable = entropyBrowser?.isAgentAvailable() ?? false;
        localSearchEnabled = isAgentAvailable && !isIncognito;
        if (!isAgentAvailable) {
            logger.info('[comet] Local search disabled - agent not available', {
                reason: 'agent_not_available',
                frontendUUID,
            });
        }
        else if (isIncognito) {
            logger.info('[comet] Local search disabled - incognito mode', {
                reason: 'incognito_mode',
                frontendUUID,
            });
        }
        if (isFirstEntry) {
            lastOmniboxInput = entropyBrowser?.getLastOmniboxInput(frontendUUID) ?? '';
        }
        if (querySource === 'default_search') {
            // Specifically block local search if the last omnibox input is a URL
            // This is to prevent https://linear.app/perplexity/issue/BRO-2791/browser-security-high-prevent-shared-links-for-automatically-executing
            if (isURL(lastOmniboxInput)) {
                localSearchEnabled = false;
                // We don't want to mark search as sidecar from untrusted default search query
                // Because sidecar bypasses all agent conditions on backend
                if (import.meta.env.VITE_IS_SIDECAR_BUILD || import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD) {
                    sidecarQuerySourceOverride = 'default_search';
                }
                logger.info('[comet] Local search disabled due to URL input in omnibox', {
                    lastOmniboxInput,
                });
            }
            if (!lastOmniboxInput) {
                /**
                 * Comet sets well-known address in referrer for hard document navigation
                 * @since 138.0
                 */
                const parsedReferrer = parseUrl(document.referrer);
                if (!parsedReferrer ||
                    parsedReferrer.origin !== location.origin ||
                    (parsedReferrer.pathname !== '/.well-known/comet-omnibox-text' &&
                        parsedReferrer.pathname !== '/onboarding-comet')) {
                    localSearchEnabled = false;
                    if (import.meta.env.VITE_IS_SIDECAR_BUILD ||
                        import.meta.env.VITE_IS_MOBILE_SIDECAR_BUILD) {
                        sidecarQuerySourceOverride = 'default_search';
                    }
                    logger.info('[comet] Local search disabled due to unsecure referer', {
                        lastOmniboxInput,
                        parsedReferrer,
                    });
                }
            }
        }
    }
    else if (querySource === 'default_search') {
        riskyQuerySource = 'external_url';
    }
    return {
        last_backend_uuid: lastBackendUUID ?? undefined,
        branch_mode: branchMode || undefined,
        read_write_token: forceNew ? undefined : threadMetadataRwToken,
        existing_entry_uuid: forceNew ? undefined : existingEntryUUID,
        attachments: newAttachments,
        language: getChosenLocale(),
        timezone: defaultTimezone,
        in_page: inPageOverride ?? inPage,
        in_domain: inDomain,
        // Note: The BE uses the length of the sources array to determine writing vs internet
        // Remove after we refactor the BE to remove search focus
        search_focus: finalSources.length === 0 ? 'writing' : 'internet',
        sources: finalSources,
        frontend_uuid: existingUuid ?? frontendUUID,
        redo_search: redoSearch,
        mode,
        target_collection_uuid: collection?.uuid,
        target_thread_access_level: resolveTargetThreadAccessLevel(targetThreadAccessLevelOverride, collection),
        model_preference: modelPreferenceSubmit ?? undefined,
        ...(isAskDeepResearch && { is_ask_deep_research: true }),
        mode_config: modeConfig,
        model_override: modelOverride,
        compare_model_preferences: compareModelPreferences && compareModelPreferences.length > 0
            ? compareModelPreferences
            : undefined,
        is_related_query: isRelatedQuery ?? false,
        is_sponsored: isSponsored ?? false,
        related_query_uuid: relatedQueryUUID,
        frontend_context_uuid: newFrontendContextUUID ?? undefined,
        prompt_source: promptSource ?? undefined,
        query_source: sidecarQuerySourceOverride ?? querySource ?? '',
        is_incognito: isIncognito,
        time_from_first_type: timeFromFirstType ?? undefined,
        local_search_enabled: localSearchEnabled,
        // schematized api params
        use_schematized_api: true,
        send_back_text_in_streaming_api: false,
        supported_block_use_cases: supportedBlockUseCases,
        utm_source: utmSource,
        client_coordinates: clientCoordinates,
        mentions,
        dsl_query: dslQuery,
        skip_search_enabled: skipSearchEnabled,
        is_nav_suggestions_disabled: isNavSuggestionsDisabled || isNavSuggestionsDisabledOverride,
        ...(querySource === 'followup' ? { followup_source: 'link' } : {}),
        ...appClientContext,
        ...(disableWidgets ? { disable_widgets: disableWidgets } : {}),
        always_search_override: alwaysSearchOverride ?? false,
        override_no_search: overrideNoSearch ?? false,
        comet_info,
        ...(queryDisplayMetadata && { query_display_metadata: queryDisplayMetadata }),
        client_search_results_cache_key: getClientSearchResultsCacheKey({
            isCometBrowser,
            frontendUUID,
            shouldUseNavV3CacheKey,
        }),
        mcp_tools: mcpTools,
        risky_query_source: riskyQuerySource,
        should_ask_for_mcp_tool_confirmation: true,
        policy_approval_decision: policyApprovalDecision,
        supports_tool_approval_modal: true,
        refinement_filters_metadata: refinementFiltersMetadata,
        experiment_overrides: Object.keys(experimentOverrides).length > 0 ? experimentOverrides : undefined,
        experiment_assignments: Object.keys(experimentAssignments).length > 0 ? experimentAssignments : undefined,
        browser_agent_allow_once_from_toggle: browserAgentAllowOnceFromToggle,
        force_enable_browser_agent: forceEnableBrowserAgent,
        force_disable_browser_agent: forceDisableBrowserAgent,
        enable_learn_about_me: enableLearnAboutMe,
        supported_features: ['browser_agent_permission_banner_v1.1'],
        extended_context: false,
        ...(inlineMode && {
            inline_mode: inlineMode,
        }),
        canonical_page_context: canonicalPageContext,
        local_workspace_directories: localWorkspaceDirectories,
        // Omitted entirely when there are none: the backend treats an empty list as
        // "a composer that had grants and sent none", which is a different fact.
        local_file_grants: localFileGrants?.length
            ? localFileGrants.map((grant) => ({
                grant_id: grant.grantId,
                file_name: grant.fileName,
                ...(grant.deviceId && { device_id: grant.deviceId }),
            }))
            : undefined,
        is_local_browser_available: isLocalBrowserAvailable,
        is_web_browser_extension_available: WEB_BROWSER_EXTENSION_QUERY_SOURCES.has(querySource)
            ? isWebBrowserExtensionAvailable
            : undefined,
        prefill_skills: pickPrefillValues(prefills, 'skill'),
        prefill_plugins: pickPrefillValues(prefills, 'plugin'),
        workflow_instructions: workflowInstructions,
        workflow_config: workflowConfig,
        workflow_key: workflowKey,
        ...(sourceMetadata && { source_metadata: { data: sourceMetadata } }),
    };
};
// The backend treats an explicit empty list as clearing sticky session prefills and an omitted
// field as leaving them alone, so a list that holds only the other kind must omit this field.
function pickPrefillValues(prefills, type) {
    if (prefills === undefined)
        return undefined;
    if (prefills.length === 0)
        return [];
    const values = prefills
        .filter((prefill) => prefill.type === type)
        .map((prefill) => prefill.value);
    return values.length > 0 ? values : undefined;
}
//# sourceMappingURL=createSubmitParams.js.map